import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  success: "bg-sage-dark/15 text-sage-dark border-sage-dark/40",
  error:   "bg-brand-darkred/12 text-brand-darkred border-brand-darkred/30",
};

export default function AdminKiUsage() {
  const [sum, setSum] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ user_email: "", kind: "", status: "", from_iso: "", to_iso: "" });

  const loadSummary = async () => {
    try {
      const r = await axios.get(`${API}/admin/ki-usage/summary`, { withCredentials: true });
      setSum(r.data);
    } catch { toast.error("Übersicht konnte nicht geladen werden."); }
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await axios.get(`${API}/admin/ki-usage`, { withCredentials: true, params });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch { toast.error("Tabelle konnte nicht geladen werden."); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSummary(); loadRows(); /* eslint-disable-next-line */ }, []);

  const downloadCsv = () => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    window.open(`${API}/admin/ki-usage/export.csv?${qs.toString()}`, "_blank", "noopener");
  };

  return (
    <div className="space-y-6" data-testid="admin-ki-usage">
      <div className="rounded-3xl border border-cream-300 bg-peach-50 p-6 md:p-8">
        <div className="text-[10.5px] uppercase tracking-[0.3em] font-semibold text-clay">
          Admin · KI-Nutzung
        </div>
        <h2 className="font-display text-2xl md:text-[28px] text-sand-800 mt-1.5 leading-tight">
          Echte Token- und Kostendaten der Pflege-KI
        </h2>
        <p className="text-sand-700 text-[13.5px] mt-2 max-w-3xl leading-relaxed">
          Jeder KI-Aufruf wird mit Modell, Token-Schätzung und Kosten in EUR/USD protokolliert.
          Keine Prompts, keine Antwort-Texte, keine Kundennamen werden gespeichert.
          Token-Werte sind <strong>Schätzungen</strong> (3.2 Zeichen ≈ 1 Token DE) — entsprechend markiert.
        </p>
      </div>

      {/* Warnungen */}
      {sum?.warnungen && (sum.warnungen.tag_überschritten || sum.warnungen.monat_überschritten) && (
        <div className="rounded-2xl border border-brand-darkred/40 bg-peach-50 p-4 text-[12.5px] text-brand-darkred" data-testid="admin-ki-warn">
          {sum.warnungen.tag_überschritten && (
            <div>Tageslimit überschritten: heute bereits {sum.kosten_heute?.cost_eur?.toFixed(2)} € (Limit {sum.warnungen.tageslimit_eur} €).</div>
          )}
          {sum.warnungen.monat_überschritten && (
            <div>Monatslimit überschritten: aktuell {sum.kosten_monat?.cost_eur?.toFixed(2)} € (Limit {sum.warnungen.monatslimit_eur} €).</div>
          )}
        </div>
      )}

      {/* KPI-Karten */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-ki-kpi">
        <Kpi label="Kosten heute"  value={fmtEur(sum?.kosten_heute?.cost_eur)} sub={`${sum?.kosten_heute?.count || 0} Aufrufe`} />
        <Kpi label="Kosten Woche"  value={fmtEur(sum?.kosten_woche?.cost_eur)} sub={`${sum?.kosten_woche?.count || 0} Aufrufe`} />
        <Kpi label="Kosten Monat"  value={fmtEur(sum?.kosten_monat?.cost_eur)} sub={`${sum?.kosten_monat?.count || 0} Aufrufe`} />
        <Kpi label="Fehlerzahl"    value={sum?.fehlerzahl ?? 0} sub={`${sum?.gesamt_aufrufe || 0} gesamt`} />
      </div>

      {/* Kosten pro Doku-Typ */}
      <div className="rounded-2xl border border-cream-300 bg-white p-5" data-testid="admin-ki-per-kind">
        <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-3">
          Kosten & Generierungen pro Dokumenttyp
        </div>
        {(sum?.per_kind || []).length === 0 ? (
          <div className="text-sand-700 text-[13px]">Noch keine Daten.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead><tr className="text-left text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 border-b border-cream-300">
                <th className="py-2 pr-3">Doku-Typ</th>
                <th className="py-2 pr-3">Aufrufe gesamt</th>
                <th className="py-2 pr-3">Aufrufe Monat</th>
                <th className="py-2 pr-3">Kosten gesamt</th>
                <th className="py-2 pr-3">Kosten Monat</th>
                <th className="py-2 pr-3">Tokens (in/out)</th>
                <th className="py-2 pr-3">Letzte Nutzung</th>
              </tr></thead>
              <tbody>
                {sum.per_kind.map((r) => (
                  <tr key={r.kind} className="border-b border-cream-100 last:border-0">
                    <td className="py-2 pr-3 text-sand-800 font-semibold">{r.label}</td>
                    <td className="py-2 pr-3 font-mono text-sand-800">{r.count_total}</td>
                    <td className="py-2 pr-3 font-mono text-sand-700">{r.count_month}</td>
                    <td className="py-2 pr-3 font-mono text-sand-800">{fmtEur(r.cost_eur_total)}</td>
                    <td className="py-2 pr-3 font-mono text-sand-700">{fmtEur(r.cost_eur_month)}</td>
                    <td className="py-2 pr-3 font-mono text-sand-600">{r.tokens_in_total} / {r.tokens_out_total}</td>
                    <td className="py-2 pr-3 text-sand-600 text-[11.5px]">{fmtDate(r.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Kosten pro User */}
      <div className="rounded-2xl border border-cream-300 bg-white p-5" data-testid="admin-ki-per-user">
        <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-3">
          Nutzung & Kosten pro User
        </div>
        {(sum?.per_user || []).length === 0 ? (
          <div className="text-sand-700 text-[13px]">Noch keine Daten.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead><tr className="text-left text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 border-b border-cream-300">
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Generierungen</th>
                <th className="py-2 pr-3">Gesamtkosten</th>
                <th className="py-2 pr-3">Tokens (in/out)</th>
                <th className="py-2 pr-3">Letzte Nutzung</th>
              </tr></thead>
              <tbody>
                {sum.per_user.map((u) => (
                  <tr key={u.user_email} className="border-b border-cream-100 last:border-0">
                    <td className="py-2 pr-3 text-sand-800">{u.user_email}</td>
                    <td className="py-2 pr-3 font-mono text-sand-800">{u.count}</td>
                    <td className="py-2 pr-3 font-mono text-sand-800">{fmtEur(u.cost_eur)}</td>
                    <td className="py-2 pr-3 font-mono text-sand-600">{u.tokens_in} / {u.tokens_out}</td>
                    <td className="py-2 pr-3 text-sand-600 text-[11.5px]">{fmtDate(u.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Filter + Tabelle */}
      <div className="rounded-2xl border border-cream-300 bg-white p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">
            Detail-Tabelle · {total} Aufrufe gesamt
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={loadRows} variant="outline" data-testid="admin-ki-reload" className="h-9 rounded-full border-cream-300 text-sand-700 hover:bg-cream-50 font-semibold px-4 text-[12.5px]">
              {loading ? "lädt …" : "Aktualisieren"}
            </Button>
            <Button onClick={downloadCsv} data-testid="admin-ki-csv" className="h-9 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white font-semibold px-4 text-[12.5px]">
              CSV-Export
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-2 mb-4">
          <input type="text" value={filters.user_email} onChange={(e) => setFilters((f) => ({ ...f, user_email: e.target.value }))}
                 placeholder="E-Mail" className="rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[12.5px]"
                 data-testid="admin-ki-filter-user" />
          <select value={filters.kind} onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))}
                  className="rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[12.5px]"
                  data-testid="admin-ki-filter-kind">
            <option value="">Alle Doku-Typen</option>
            {(sum?.per_kind || []).map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className="rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[12.5px]"
                  data-testid="admin-ki-filter-status">
            <option value="">Alle Status</option>
            <option value="success">success</option>
            <option value="error">error</option>
          </select>
          <input type="date" value={filters.from_iso?.slice(0,10) || ""} onChange={(e) => setFilters((f) => ({ ...f, from_iso: e.target.value ? `${e.target.value}T00:00:00+00:00` : "" }))}
                 className="rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[12.5px]"
                 data-testid="admin-ki-filter-from" />
          <input type="date" value={filters.to_iso?.slice(0,10) || ""} onChange={(e) => setFilters((f) => ({ ...f, to_iso: e.target.value ? `${e.target.value}T23:59:59+00:00` : "" }))}
                 className="rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[12.5px]"
                 data-testid="admin-ki-filter-to" />
        </div>

        {rows.length === 0 ? (
          <div className="text-sand-700 text-[13px] py-3" data-testid="admin-ki-empty">Noch keine Einträge.</div>
        ) : (
          <div className="overflow-x-auto" data-testid="admin-ki-table">
            <table className="w-full text-[12px]">
              <thead><tr className="text-left text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 border-b border-cream-300">
                <th className="py-2 pr-3">Datum</th>
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Doku-Typ</th>
                <th className="py-2 pr-3">Modell</th>
                <th className="py-2 pr-3 text-right">Tokens in</th>
                <th className="py-2 pr-3 text-right">Tokens out</th>
                <th className="py-2 pr-3 text-right">Kosten EUR</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Genauigkeit</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-cream-100 last:border-0">
                    <td className="py-2 pr-3 text-sand-700 whitespace-nowrap text-[11.5px]">{fmtDate(r.created_at)}</td>
                    <td className="py-2 pr-3 text-sand-800">{r.user_email}</td>
                    <td className="py-2 pr-3 text-sand-800">{r.kind_label || r.kind}</td>
                    <td className="py-2 pr-3 text-sand-600 text-[11px]">{r.model}</td>
                    <td className="py-2 pr-3 font-mono text-sand-800 text-right">{r.tokens_input}</td>
                    <td className="py-2 pr-3 font-mono text-sand-800 text-right">{r.tokens_output}</td>
                    <td className="py-2 pr-3 font-mono text-sand-800 text-right">{fmtEur(r.cost_eur)}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex items-center px-2 h-5 rounded-full border text-[10.5px] uppercase tracking-[0.18em] font-semibold ${STATUS_TONE[r.status] || ""}`}>
                        {r.status}
                      </span>
                      {r.status === "error" && r.error && (
                        <div className="text-[10.5px] text-brand-darkred mt-1 max-w-[260px] truncate" title={r.error}>{r.error}</div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-[10.5px] text-sand-600">{r.tokens_estimated ? "geschätzt" : "echt"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-cream-300 bg-white p-4">
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-sand-500 font-semibold">{label}</div>
      <div className="font-display text-2xl text-sand-800 leading-tight mt-1">{value}</div>
      {sub && <div className="text-[11px] text-sand-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function fmtEur(v) {
  if (v === null || v === undefined) return "–";
  const n = Number(v);
  if (Number.isNaN(n)) return "–";
  return n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + " €";
}

function fmtDate(iso) {
  if (!iso) return "–";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}
